// src/components/Sidebar.tsx
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  IconLayoutDashboard,
  IconUsers,
  IconRocket,
  IconListDetails,
  IconHeadset,
  IconChartBar,
  IconSettings,
  IconAdjustmentsHorizontal,
  IconCreditCard,
  IconWorld,
  IconShieldCheck,
  IconServerCog,
  IconActivity,
  IconTool,
  IconFileCode,
  IconClipboardList,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";
import { PLATFORM_LOGOS, FALLBACK_LOGOS } from "../../config/platformLogos";

interface SidebarProps {
  isOpen: boolean;
  isMobile: boolean;
  setIsOpen: (open: boolean) => void;
}

// Definir tipos para los items del menú
type MenuItem = {
  path?: string;
  name: string;
  icon: any;
  effectKey?: string;
  isDropdown?: boolean;
  dropdownName?: string;
  items?: MenuItem[];
};

const iconEffects: Record<string, string> = {
  dashboard: "group-hover:scale-110 group-hover:text-brand-primary",
  users: "group-hover:scale-125 group-hover:text-brand-primary",
  rocket:
    "group-hover:-translate-y-1 group-hover:rotate-12 group-hover:text-state-warning",
  headset:
    "group-hover:scale-110 group-hover:-rotate-6 group-hover:text-brand-secondary",
  chart: "group-hover:scale-110 group-hover:text-state-success",
  settings:
    "group-hover:rotate-90 group-hover:text-content-primary",
  shield: "group-hover:scale-110 group-hover:text-state-success",
  server:
    "group-hover:rotate-6 group-hover:scale-110 group-hover:text-state-info",
  activity: "group-hover:text-state-success group-hover:scale-125",
  list: "group-hover:scale-110 group-hover:text-brand-secondary",
  credit: "group-hover:scale-110 group-hover:text-state-warning",
  globe: "group-hover:rotate-12 group-hover:text-brand-primary",
  tool: "group-hover:rotate-45 group-hover:text-state-warning",
  code: "group-hover:scale-110 group-hover:text-brand-secondary",
};

const Sidebar: React.FC<SidebarProps> = ({ isOpen, isMobile, setIsOpen }) => {
  const { user, hasPermission } = useAuth();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const toggleDropdown = (name: string) => {
    if (!isOpen) {
      setIsOpen(true);
      setOpenDropdown(name);
    } else {
      setOpenDropdown(openDropdown === name ? null : name);
    }
  };

  const closeOnMobileNavigation = () => {
    if (isMobile) setIsOpen(false);
  };

  // ============================================================
  // ✅ FUNCIONES DE PERMISOS (BASADAS EN hasPermission)
  // ============================================================

  const canSeeDashboard = () => hasPermission("dashboard_access");
  const canSeeClientes = () => hasPermission("clients_access");
  const canSeeOnboarding = () => hasPermission("onboarding_access");
  const canSeeSuscripciones = () => hasPermission("subscriptions_access");
  const canSeeSoporte = () => hasPermission("support_access");
  const canSeeAnalitica = () => hasPermission("analytics_access");
  const canSeeConfiguracionComercial = () =>
    hasPermission("commercial_config_access");
  const canSeeConfiguracionGeneral = () =>
    hasPermission("general_config_access");
  const canSeeConfiguracionGlobal = () => hasPermission("global_config_access");
  const canSeeConfiguracionSeguridad = () =>
    hasPermission("security_config_access");
  const canSeeInfraestructuraEstado = () =>
    hasPermission("infrastructure_status_access");
  const canSeeInfraestructuraMantenimiento = () =>
    hasPermission("infrastructure_maintenance_access");
  const canSeeInfraestructuraLogs = () =>
    hasPermission("infrastructure_logs_access");
  const canSeeAuditoria = () => hasPermission("audit_access");

  // ============================================================
  // CONSTRUCCIÓN DE MENÚ DINÁMICO
  // ============================================================

  const menuSections: { label: string; items: MenuItem[] }[] = [];

  // 1. Dashboard
  if (canSeeDashboard()) {
    menuSections.push({
      label: "",
      items: [
        {
          path: "/",
          name: "Dashboard",
          icon: IconLayoutDashboard,
          effectKey: "dashboard",
        },
      ],
    });
  }

  // 2. Clientes
  const clientesItems: MenuItem[] = [];
  if (canSeeClientes())
    clientesItems.push({
      path: "/clientes",
      name: "Clientes",
      icon: IconUsers,
      effectKey: "users",
    });
  if (canSeeOnboarding())
    clientesItems.push({
      path: "/onboarding",
      name: "Onboarding",
      icon: IconRocket,
      effectKey: "rocket",
    });
  if (canSeeSuscripciones())
    clientesItems.push({
      path: "/suscripciones",
      name: "Suscripciones",
      icon: IconListDetails,
      effectKey: "list",
    });
  if (canSeeSoporte())
    clientesItems.push({
      path: "/soporte",
      name: "Soporte",
      icon: IconHeadset,
      effectKey: "headset",
    });
  if (canSeeAnalitica()) {
    clientesItems.push({
      path: "/analitica",
      name: "Analítica",
      icon: IconChartBar,
      effectKey: "chart",
    });
  }

  if (clientesItems.length > 0) {
    menuSections.push({
      label: "Clientes",
      items: clientesItems,
    });
  }

  // 3. Sistema
  const sistemaItems: MenuItem[] = [];

  // 3a. Configuración
  const configuracionItems: MenuItem[] = [];
  if (canSeeConfiguracionGeneral())
    configuracionItems.push({
      path: "/configuracion/general",
      name: "General",
      icon: IconAdjustmentsHorizontal,
      effectKey: "settings",
    });
  if (canSeeConfiguracionComercial())
    configuracionItems.push({
      path: "/configuracion/comercial",
      name: "Comercial",
      icon: IconCreditCard,
      effectKey: "credit",
    });
  if (canSeeConfiguracionGlobal())
    configuracionItems.push({
      path: "/configuracion/global",
      name: "Global",
      icon: IconWorld,
      effectKey: "globe",
    });
  if (canSeeConfiguracionSeguridad())
    configuracionItems.push({
      path: "/configuracion/seguridad",
      name: "Seguridad",
      icon: IconShieldCheck,
      effectKey: "shield",
    });

  if (configuracionItems.length > 0) {
    sistemaItems.push({
      name: "Configuración",
      isDropdown: true,
      icon: IconSettings,
      dropdownName: "configuracion",
      items: configuracionItems,
      effectKey: "settings",
    });
  }

  // 3b. Infraestructura
  const infraItems: MenuItem[] = [];
  if (canSeeInfraestructuraEstado())
    infraItems.push({
      path: "/plataforma/estado",
      name: "Estado",
      icon: IconActivity,
      effectKey: "activity",
    });
  if (canSeeInfraestructuraMantenimiento())
    infraItems.push({
      path: "/plataforma/mantenimiento",
      name: "Mantenimiento",
      icon: IconTool,
      effectKey: "tool",
    });
  if (canSeeInfraestructuraLogs())
    infraItems.push({
      path: "/plataforma/logs",
      name: "Logs",
      icon: IconFileCode,
      effectKey: "code",
    });

  if (infraItems.length > 0) {
    sistemaItems.push({
      name: "Infraestructura",
      isDropdown: true,
      icon: IconServerCog,
      dropdownName: "plataforma",
      items: infraItems,
      effectKey: "server",
    });
  }

  // 3c. Auditoría
  if (canSeeAuditoria()) {
    sistemaItems.push({
      path: "/auditoria",
      name: "Auditoría",
      icon: IconClipboardList,
      effectKey: "list",
    });
  }

  if (sistemaItems.length > 0) {
    menuSections.push({
      label: "Sistema",
      items: sistemaItems,
    });
  }

  // ============================================================
  // RENDERIZADO
  // ============================================================

  const renderNavItem = (item: MenuItem, idx: number) => {
    if (item.isDropdown) {
      return (
        <div key={idx} className="mb-2">
          <button
            onClick={() => toggleDropdown(item.dropdownName!)}
            className={`group flex min-h-[var(--navigation-item-height)] w-full items-center rounded-ui px-3 transition-colors
              ${
                openDropdown === item.dropdownName
                  ? "bg-brand-soft text-brand-primary"
                  : "text-content-secondary hover:bg-surface-hover hover:text-content-primary"
              }
            `}
          >
            <item.icon
              className={`
                w-6 h-6
                flex-shrink-0
                transition-all duration-300
                ${iconEffects[item.effectKey || ""] || "group-hover:scale-110"}
                ${isOpen ? "mr-3" : "mx-auto"}
              `}
            />

            {isOpen && (
              <>
                <span className="text-xs font-bold truncate flex-1 text-left animate-in fade-in slide-in-from-left-2 duration-300">
                  {item.name}
                </span>
                <IconChevronRight
                  className={`w-4 h-4 transition-transform duration-200 ${openDropdown === item.dropdownName ? "rotate-90" : ""}`}
                />
              </>
            )}
          </button>

          {isOpen && openDropdown === item.dropdownName && (
            <div className="ml-8 mt-1 space-y-1 border-l-2 border-border pl-3 animate-in slide-in-from-left-2 fade-in duration-300">
              {item.items?.map((subItem: MenuItem, subIdx: number) => (
                <NavLink
                  key={subIdx}
                  to={subItem.path!}
                  onClick={closeOnMobileNavigation}
                  className={({ isActive }) => `
                    flex min-h-[var(--navigation-subitem-height)] items-center rounded-ui px-3 transition-colors text-sm
                    ${
                      isActive
                        ? "bg-brand-soft text-brand-primary"
                        : "text-content-secondary hover:bg-surface-hover hover:text-content-primary"
                    }
                  `}
                >
                  <subItem.icon
                    className={`
                      w-4 h-4 mr-3 flex-shrink-0
                      transition-all duration-300
                      ${iconEffects[subItem.effectKey || ""] || "group-hover:scale-110"}
                    `}
                  />
                  <span className="text-xs font-medium truncate tracking-tight">
                    {subItem.name}
                  </span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <NavLink
        key={idx}
        to={item.path!}
        onClick={closeOnMobileNavigation}
        className={({ isActive }) => `
          group relative flex min-h-[var(--navigation-item-height)] items-center rounded-ui px-3 transition-colors
          ${
            isActive
              ? "bg-brand-soft text-brand-primary"
              : "text-content-secondary hover:bg-surface-hover hover:text-content-primary"
          }
        `}
      >
        <item.icon
          className={`
            w-6 h-6
            flex-shrink-0
            transition-all duration-300
            ${iconEffects[item.effectKey || ""] || "group-hover:scale-110"}
            ${isOpen ? "mr-3" : "mx-auto"}
          `}
        />
        {isOpen && (
          <span className="text-xs font-bold truncate animate-in fade-in slide-in-from-left-2 duration-300">
            {item.name}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <aside
      id="primary-navigation"
      className={`fixed left-0 top-0 z-30 h-dvh border-r border-border bg-surface shadow-md transition-[transform,width] duration-300 ${
        isMobile
          ? isOpen
            ? "w-[200px] translate-x-0"
            : "w-[200px] -translate-x-full"
          : isOpen
            ? "w-[var(--navigation-sidebar-width)] translate-x-0"
            : "w-[var(--navigation-sidebar-collapsed-width)] translate-x-0"
      }`}
    >
      <div className="flex flex-col h-full relative">
        {/* BOTÓN DE COLAPSO */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          type="button"
          aria-label={isOpen ? "Contraer menú" : "Expandir menú"}
          aria-expanded={isOpen}
          className="absolute -right-4 top-[var(--navigation-header-height)] z-50 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-ui border dark:border-blue-800 bg-surface text-brand-primary shadow-sm transition-colors hover:border-brand-primary hover:bg-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 dark:hover:bg-surface-active dark:focus-visible:bg-surface-active md:inline-flex"
        >
          {isOpen ? (
            <IconChevronLeft className="w-4 h-4" />
          ) : (
            <IconChevronRight className="w-4 h-4" />
          )}
        </button>

        {/* LOGO */}
        <div className="flex h-[var(--navigation-header-height)] items-center border-b border-border px-5">
          <div className="flex items-center w-full">
            <img
              src={PLATFORM_LOGOS.principal.claro}
              alt="Logo CISTCOR"
              className={`block dark:hidden transition-all duration-500 object-contain ${
                isOpen ? "w-auto h-10 max-w-[160px]" : "w-10 h-10"
              }`}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = FALLBACK_LOGOS.claro;
              }}
            />
            <img
              src={PLATFORM_LOGOS.principal.oscuro}
              alt="Logo CISTCOR"
              className={`hidden dark:block transition-all duration-500 object-contain ${
                isOpen ? "w-auto h-10 max-w-[160px]" : "w-10 h-10"
              }`}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = FALLBACK_LOGOS.oscuro;
              }}
            />
          </div>
        </div>

        {/* MENU ITEMS */}
        <nav className="flex-1 overflow-y-auto py-6 px-1 scrollbar-none">
          {menuSections.map((section, sIdx) => (
            <div key={sIdx} className="mb-6">
              {isOpen && section.label && (
                <h2 className="mb-3 px-4 text-[10px] text-brand-primary font-bold uppercase tracking-[0.2em] animate-in fade-in">
                  {section.label}
                </h2>
              )}
              <div className="space-y-1.5">
                {section.items?.map((item, idx) => {
                  if (item.isDropdown) {
                    return renderNavItem(item, idx);
                  }
                  if (item.path) {
                    return renderNavItem(item, idx);
                  }
                  return null;
                })}
              </div>
            </div>
          ))}
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
